import { PayloadAction } from "@reduxjs/toolkit";
import { ICustomizeColumns } from "app/types";
import { set } from "lodash";
import { createSlice } from "utils/@reduxjs/toolkit";
import { GroupByTypeEnum } from "../../../types";
import {
  IBoardState,
  IExportSettings,
  ExportBoardAction,
  IMultipleShorting,
} from "../types";
import {
  deriveAppliedFilterIds,
  filterValueToStored,
  getFilterValuesKey,
} from "../../FilterBar/filterReduxHelpers";
import { IBoardFilterValues, IBoardsFilters } from "../types";
import { FilterPickerType, FilterSelectedValue } from "../../../types";
import {
  defaultBoardForm,
  initialFilterValues,
  initialFiltersValues,
  initialState,
} from "./initialState";
import { TicketGroup } from "../../../containers/Tickets/types";

const normalizeBoardFilters = (
  filters?: IBoardsFilters | null,
): IBoardsFilters => {
  const merged: IBoardsFilters = {
    ...initialFiltersValues,
    ...filters,
    filters: {
      ...initialFilterValues,
      ...(filters?.filters ?? {}),
    } as IBoardFilterValues,
    appliedFilterIds: filters?.appliedFilterIds ?? [],
  };

  if (!merged.appliedFilterIds.length) {
    merged.appliedFilterIds = deriveAppliedFilterIds(merged.filters);
  }

  return merged;
};

const useClinicSlice = createSlice({
  name: "boardSetting",
  initialState,
  reducers: {
    toggleBoardSetting: (state, action: PayloadAction<boolean>) => {
      state.isShownBoardSetting = action.payload;
    },
    toggleBoardDraftFormModal: (state, action: PayloadAction<boolean>) => {
      state.boardDraftFormModal = action.payload;
    },
    // get board request
    fetchBoardDetails: (
      state,
      action: PayloadAction<{
        id: string;
        callback: (
          groupBy: GroupByTypeEnum,
          subGroupBy: GroupByTypeEnum,
        ) => void;
      }>,
    ) => {
      state.isBoardLoading = true;
    },
    updateBoard: (
      state,
      action: PayloadAction<{ id: string; callback: () => void }>,
    ) => {
      state.isBoardCreate = true;
    },

    setBoardLoading: (state, action: PayloadAction<boolean>) => {
      state.isBoardLoading = action.payload;
    },
    setIsBoardCreate: (state, action: PayloadAction<boolean>) => {
      state.isBoardCreate = action.payload;
    },
    setBoardFormData: (state, action: PayloadAction<IBoardState>) => {
      state.boardForm = {
        ...action.payload,
        filters: normalizeBoardFilters(action.payload.filters),
      };
    },
    setDraftBoardFormData: (state, action: PayloadAction<IBoardState>) => {
      state.draftBoardForm = {
        ...action.payload,
        filters: normalizeBoardFilters(action.payload.filters),
      };
    },
    clearDraftFrom: (state) => {
      state.draftBoardForm = state.boardForm;
      state.draftBoardForm.groupJson = state.boardForm.groupJson;
    },
    setBoardCustomizeCards: (
      state,
      action: PayloadAction<Array<ICustomizeColumns>>,
    ) => {
      state.draftBoardForm.customizeCards = action.payload;
    },
    setClearBoardForm: (state) => {
      state.boardForm = { ...defaultBoardForm };
    },
    setClearDraftFrom: (state) => {
      state.draftBoardForm = { ...defaultBoardForm };
    },
    setBoardsFormValue: (
      state,
      action: PayloadAction<{ name: string; value: string }>,
    ) => {
      set(state, `draftBoardForm.${action.payload.name}`, action.payload.value);
    },
    setDefaultTicketValue: (
      state,
      action: PayloadAction<{ key: string; value: any }>,
    ) => {
      const { key, value } = action.payload;
      const values = state.draftBoardForm.defaultTicketValues || [];
      const index = values.findIndex(
        (item) =>
          item?.id === key || item?.name === key || item?.hasOwnProperty?.(key),
      );

      if (index !== -1) {
        values[index] = { [key]: value };
      } else {
        values.push({ [key]: value });
      }
      state.draftBoardForm.defaultTicketValues = values;
    },
    removeDefaultTicketValue: (
      state,
      action: PayloadAction<{ key: string }>,
    ) => {
      const { key } = action.payload;
      const values: any[] = state.draftBoardForm.defaultTicketValues || [];
      const filteredValues = values.filter(
        (item) =>
          !(typeof item?.id === "string" && item.id.includes(key)) &&
          !(typeof item?.name === "string" && item.name.includes(key)) &&
          !item?.hasOwnProperty?.(key),
      );
      state.draftBoardForm.defaultTicketValues = filteredValues;
    },

    setBoardsGroupVisibility: (state, action: PayloadAction<number>) => {
      state.draftBoardForm.visibleGroups[action.payload].isVisible =
        !state.draftBoardForm.visibleGroups[action.payload].isVisible;
    },
    setBoardsGroupAllVisibility: (state, action: PayloadAction<boolean>) => {
      state.draftBoardForm.visibleGroups.forEach((item) => {
        item.isVisible = action.payload;
      });
    },
    setVisibleGroups: (state, action: PayloadAction<TicketGroup[]>) => {
      state.draftBoardForm.visibleGroups = action.payload;
    },
    setBoardsCustomizeCard: (state, action: PayloadAction<GroupByTypeEnum>) => {
      const index = state.draftBoardForm.customizeCards.findIndex(
        (card) => card.type === action.payload,
      );

      if (index !== -1) {
        state.draftBoardForm.customizeCards[index].isVisible =
          !state.draftBoardForm.customizeCards[index].isVisible;
      }
    },
    setBoardsCustomizeCardAllVisibility: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.draftBoardForm.customizeCards.forEach((item) => {
        if (!item.disabled) {
          item.isVisible = action.payload;
        }
      });
    },
    setCustomizeCardOrder: (
      state,
      action: PayloadAction<ICustomizeColumns[]>,
    ) => {
      state.draftBoardForm.customizeCards = action.payload;
    },

    //group by json field
    updateBoardsGroupJson: (
      state,
      action: PayloadAction<{ boardId: string; callback: () => void }>,
    ) => {},
    setBoardGroupJson: (
      state,
      action: PayloadAction<{ groupJson: string }>,
    ) => {
      const { groupJson } = action.payload;
      state.draftBoardForm.groupJson = groupJson;
      state.boardForm.groupJson = groupJson;
    },
    updateGroupBy: (
      state,
      action: PayloadAction<{ id: string; callback: () => void }>,
    ) => {
      state.isBoardUpdate = true;
    },
    setIsBoardUpdate: (state, action: PayloadAction<boolean>) => {
      state.isBoardUpdate = action.payload;
    },
    updateTicketType: (
      state,
      action: PayloadAction<{ id: string; callback: () => void }>,
    ) => {
      state.isBoardUpdate = true;
    },
    updateCustomizeCard: (
      state,
      action: PayloadAction<{
        id: string;
        callback: () => void;
        silent?: boolean;
      }>,
    ) => {
      state.isBoardUpdate = true;
    },
    updateDefaultTicketValues: (
      state,
      action: PayloadAction<{ id: string; callback: () => void }>,
    ) => {
      state.isBoardUpdate = true;
    },
    setTicketDropdownSearchKeyword: (state, action: PayloadAction<string>) => {
      state.ticketDropdownSearchKeyword = action.payload;
    },
    exportBoard: (state, action: PayloadAction<ExportBoardAction>) => {
      state.isExportingBoard = true;
    },
    setIsExportingBoard: (state, action: PayloadAction<boolean>) => {
      state.isExportingBoard = action.payload;
    },
    setExportSettings: (
      state,
      action: PayloadAction<Partial<IExportSettings>>,
    ) => {
      state.exportSettings = { ...state.exportSettings, ...action.payload };
    },

    /// boards shorting
    setBoardsShorting: (state, action: PayloadAction<IMultipleShorting>) => {
      state.boardsShorting = action.payload;
    },
    resetFilters: (state) => {
      state.draftBoardForm.filters = initialFiltersValues;
    },
    updateBoardFilterValue: (
      state,
      action: PayloadAction<{
        filterId: string;
        value: FilterSelectedValue;
        pickerType: FilterPickerType;
      }>,
    ) => {
      const valuesKey = getFilterValuesKey(action.payload.filterId);
      if (!valuesKey) return;

      if (!state.draftBoardForm.filters.filters) {
        state.draftBoardForm.filters.filters = initialFilterValues;
      }

      state.draftBoardForm.filters.filters![valuesKey] = filterValueToStored(
        action.payload.value,
        action.payload.pickerType,
      );
    },
    addBoardAppliedFilter: (state, action: PayloadAction<string>) => {
      const appliedFilterIds = state.draftBoardForm.filters.appliedFilterIds ?? [];

      if (!appliedFilterIds.includes(action.payload)) {
        state.draftBoardForm.filters.appliedFilterIds = [
          ...appliedFilterIds,
          action.payload,
        ];
      }
    },
    removeBoardAppliedFilter: (state, action: PayloadAction<string>) => {
      const valuesKey = getFilterValuesKey(action.payload);

      state.draftBoardForm.filters.appliedFilterIds = (
        state.draftBoardForm.filters.appliedFilterIds ?? []
      ).filter((filterId) => filterId !== action.payload);

      if (valuesKey && state.draftBoardForm.filters.filters) {
        state.draftBoardForm.filters.filters[valuesKey] = [];
      }
    },
    commitBoardFilters: (state) => {
      state.boardForm.filters = JSON.parse(
        JSON.stringify(state.draftBoardForm.filters),
      );
    },
    saveBoardFilters: (
      state,
      _action: PayloadAction<{ id: string; callback?: () => void }>,
    ) => {
      state.isBoardUpdate = true;
    },
  },
});

export const { reducer, actions, name: sliceKey } = useClinicSlice;
